// @flow
import moment from 'moment';
import populateRequiredImmunizationDueDates from './immunizationUtil';

export type Update = {
  _id: string,
  generalInfo: string,
  houseHistory: Object[],
  update: string,          // a description of the update
  type: string,            // the type of the update (see below)
  due: string,             // formatted as DD-MM-YYYY
  overdue: boolean,
}

export const UPDATE_TYPES = {
  WCC: 'WCC',
  IMMUNIZATION: 'IMMUNIZATION',
  HIV: 'HIV',
  GROWTH: 'GROWTH',
  HEMOGLOBIN: 'HEMOGLOBIN',
  CUSTOM: 'CUSTOM',
};

export function getUpdateLink(patientId: string, updateType: string) {
  let link = `/patient/${patientId}`;
  if (updateType === UPDATE_TYPES.WCC) {
    link += '/wellChildChecks';
  } else if (updateType === UPDATE_TYPES.IMMUNIZATION) {
    link += '/immunizations';
  } else if (updateType === UPDATE_TYPES.HIV
    || updateType === UPDATE_TYPES.HEMOGLOBIN) {
    link += '/testing';
  } else if (updateType === UPDATE_TYPES.GROWTH) {
    link += '/growthCharts';
  } else {
    // unknown update type
  }
  return link;
}

// Generates an Update object with the given arguments. The non-patient fields
// correspond to the Update object.
function generateUpdate(patient: Object, update: string, dateDue: moment, type: string, overdue: boolean): Update {
  return {
    _id: patient._id,
    generalInfo: patient.generalInfo,
    houseHistory: patient.houseHistory,
    update,
    type,
    due: dateDue,
    overdue,
  };
}

// Returns whether or not the given due date is overdue or within update range.
function inRange(dateDue: moment, updateRange: number): boolean {
  const now = moment();
  const timeUntil = moment.duration(dateDue.diff(now));
  return dateDue < now || timeUntil < moment.duration(updateRange, 'days');
}

// Finds incomplete upcoming WCC updates for the given patient (if any) and
// appends it to the input updates list.
function findWccUpdates(updates: Update[], patient: Object, updateRange: number): void {
  if (!patient.generalInfo.dob) {
    return;
  }

  const intervalRegex = /\d+ (Month|Year)/;
  patient.wellChildChecks.forEach((wcc) => {
    if (wcc.date || wcc.overridden) {
      return;
    }
    const dob = moment(patient.generalInfo.dob);
    const now = moment();
    const { age } = wcc;
    // Exception: "16-23 Months" due date should be 18 months.
    const ageInterval = age === '16-23 Months'
      ? '18 months'
      : age.match(intervalRegex)[0];
    const [amount, unit] = ageInterval.split(' ');
    const dateDue = dob.add(amount, unit);
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, `${age} WCC`, dateDue,
        UPDATE_TYPES.WCC, overdue));
    }
  });
}

// Finds incomplete upcoming immunization updates for the given patient (if
// any) and appends it to the input updates list.
function findImmunizationUpdates(updates: Update[], patient: Object, updateRange: number): void {
  const requiredImmunizations = populateRequiredImmunizationDueDates(patient);
  const allImmunizations = requiredImmunizations.concat(patient.additionalImmunizations);
  allImmunizations.forEach((immunization) => {
    const dateDue = moment(immunization.dateDue);
    const now = moment();

    // breadcrumb: hpv-females-only
    if (patient.generalInfo.sex === 'M' && (immunization.immunization === 'HPV 1' || immunization.immunization === 'HPV 2')) {
      return;
    }

    if (immunization.dateAdministered || immunization.overridden || !dateDue) {
      return;
    }

    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, `${immunization.immunization}`,
        dateDue, UPDATE_TYPES.IMMUNIZATION, overdue));
    }
  });
}

// Finds incomplete upcoming hiv updates for the given patient (if any) and
// appends it to the input updates list.
function findHivUpdates(updates: Update[], patient: Object, updateRange: number): void {
  if (!patient.generalInfo.dateArrived) {
    return;
  }

  const { admissionHivTest0, admissionHivTest6 } = patient.testing;
  const now = moment();
  const dateDue0 = moment(patient.generalInfo.dateArrived);
  if (admissionHivTest0 && !admissionHivTest0.date
    && !admissionHivTest0.overridden && inRange(dateDue0, updateRange)) {
    const overdue = dateDue0 < now;
    updates.push(generateUpdate(patient, 'HIV Test', dateDue0, UPDATE_TYPES.HIV,
      overdue));
  }
  const dateDue6 = moment(patient.generalInfo.dateArrived).add(6, 'weeks');
  if (admissionHivTest6 && !admissionHivTest6.date
    && !admissionHivTest6.overridden && inRange(dateDue6, updateRange)) {
    const overdue = dateDue6 < now;
    updates.push(generateUpdate(patient, 'HIV Test', dateDue6, UPDATE_TYPES.HIV,
      overdue));
  }

  patient.testing.hivTests.forEach((test) => {
    if (test.date || test.overridden || !test.dateDue) {
      return;
    }
    const dateDue = moment(test.dateDue);
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, 'HIV Test', dateDue,
        UPDATE_TYPES.HIV, overdue));
    }
  });
}

// Finds incomplete upcoming growth chart updates for the given patient
// (if any) and appends it to the input updates list.
function findGrowthUpdates(updates: Update[], patient: Object, updateRange: number): void {
  if (!patient.generalInfo.dob) {
    return;
  }

  patient.requiredMeasurements.forEach((measurement, i) => {
    if (measurement.weight || measurement.overridden) {
      return;
    }
    const dob = moment(patient.generalInfo.dob);
    const now = moment();
    const dateDue = dob.add(i, 'month');
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, 'Growth Measurement', dateDue,
        UPDATE_TYPES.GROWTH, overdue));
    }
  });
}

// Finds incomplete upcoming hemoglobin test updates for the given patient
// (if any) and appends it to the input updates list.
function findHemoglobinUpdates(updates: Update[], patient: Object, updateRange: number): void {
  const admissionTest = patient.testing.admissionHemoglobinTest;
  if (patient.generalInfo.dateArrived && !admissionTest.dateAdministered
    && !admissionTest.overridden) {
    const now = moment();
    const dateDue = moment(patient.generalInfo.dateArrived);
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, 'Hemoglobin Test', dateDue,
        UPDATE_TYPES.HEMOGLOBIN, overdue));
    }
  }
  patient.testing.hemoglobinTests.forEach((test) => {
    if (test.dateAdministered || test.overridden) {
      return;
    }
    const now = moment();
    const dateDue = moment(test.dateDue);
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, 'Hemoglobin Test', dateDue,
        UPDATE_TYPES.HEMOGLOBIN, overdue));
    }
  });
}

// Finds incomplete upcoming custom updates for the given patient (if any) and
// appends it to the input updates list.
function findCustomUpdates(updates: Update[], patient: Object, updateRange: number): void {
  patient.customUpdates.forEach((update) => {
    if (update.dateCompleted) {
      return;
    }
    const now = moment();
    const dateDue = moment(update.dateDue);
    const { task } = update;
    if (inRange(dateDue, updateRange)) {
      const overdue = dateDue < now;
      updates.push(generateUpdate(patient, `Custom Update: ${task}`,
        dateDue, UPDATE_TYPES.CUSTOM, overdue));
    }
  });
}

// Finds upcoming updates of all types and appends them to the input updates
// list.
export function findAllUpdates(updates: Update[], patient: Object, updateRange: number, includeCustom: boolean): void {
  findWccUpdates(updates, patient, updateRange);
  findImmunizationUpdates(updates, patient, updateRange);
  findHivUpdates(updates, patient, updateRange);
  findGrowthUpdates(updates, patient, updateRange);
  findHemoglobinUpdates(updates, patient, updateRange);
  if (includeCustom) {
    findCustomUpdates(updates, patient, updateRange);
  }
}



// WEBPACK FOOTER //
// ./client/src/util/updatesUtil.js